import { GlobalConstants } from "../components/constants/GlobalConstants";
import {
  localStorageSave,
  localStorageGet,
  isJson,
  isEmptyValue,
  base64url_encode,
  getUniqueGridIdString,
  getParsedPropItemFromLegacy
} from "../helpers/GlobalHelper";
import { useReducer, useEffect, useState, useMemo } from "react";
import { IInfoMessage } from "../components/interfaces/Lms";
import { convertCamelCase } from "../Helpers";
import { useLmsCacheSelector } from "./UseLmsCacheSelector";
import { useLmsStoreSelector } from "./UseLmsStoreSelector";
import { FeatureFlags } from "../components/constants/LocalServerConstants";
import { useLMSGlobalReducer } from "../components/lms/LmsGlobalStore";

interface ILmsLmsGlobalFilterOptions {
  metaDataSuffix?: string;
  storageId?: string;
  filterKeysList?: string[];
  initialValues?: any;
  useApiFiltering?: boolean;
  gridUniqueIdString?: string;
}

const filterNumber = (activeAmountFilter, dataColumnValue, operator) => {
  let isMatch = false;
  const dataColumnValueFloat = parseFloat(dataColumnValue);
  const activeAmountFilterValue = parseFloat(activeAmountFilter);

  if (operator === "isnull") {
    return dataColumnValue === null;
  }

  if (operator === "isnotnull") {
    return dataColumnValue !== null;
  }

  if (operator === "eq") {
    isMatch = activeAmountFilterValue === dataColumnValueFloat;
  }

  if (operator === "neq") {
    isMatch = activeAmountFilterValue !== dataColumnValueFloat;
  }

  if (operator === "gte") {
    isMatch = activeAmountFilterValue <= dataColumnValueFloat;
  }

  if (operator === "gt") {
    isMatch = activeAmountFilterValue < dataColumnValueFloat;
  }

  if (operator === "lte") {
    isMatch = activeAmountFilterValue >= dataColumnValueFloat;
  }

  if (operator === "lt") {
    isMatch = activeAmountFilterValue > dataColumnValueFloat;
  }

  return isMatch;
};

export const filterDate = (activeDateFilterValue, dataColumnValue, operator) => {
  let isMatch = false;
  const dataColumnValueString = dataColumnValue?.includes("T") ? dataColumnValue.split("T")[0] : dataColumnValue;
  const dataColumnValueMilliseconds = new Date(dataColumnValueString).getTime();
  const optionValueMilliseconds = new Date(activeDateFilterValue).getTime();

  if (operator === "isnull") {
    return dataColumnValue === null;
  }

  if (operator === "isnotnull") {
    return dataColumnValue !== null;
  }

  if (dataColumnValue !== undefined) {
    if (operator === "eq") {
      isMatch = optionValueMilliseconds === dataColumnValueMilliseconds;
    }

    if (operator === "neq") {
      isMatch = optionValueMilliseconds !== dataColumnValueMilliseconds;
    }

    if (operator === "gte") {
      isMatch = optionValueMilliseconds <= dataColumnValueMilliseconds;
    }

    if (operator === "gt") {
      isMatch = optionValueMilliseconds < dataColumnValueMilliseconds;
    }

    if (operator === "lte") {
      isMatch = optionValueMilliseconds >= dataColumnValueMilliseconds;
    }

    if (operator === "lt") {
      isMatch = optionValueMilliseconds > dataColumnValueMilliseconds;
    }

    if (operator === "neq") {
      isMatch = optionValueMilliseconds !== dataColumnValueMilliseconds;
    }

    if (operator === "before") {
      isMatch = optionValueMilliseconds > dataColumnValueMilliseconds;
    }

    if (operator === "after") {
      isMatch = optionValueMilliseconds < dataColumnValueMilliseconds;
    }
  }

  return isMatch;
};

const filterText = (activeFilterValue, dataColumnValue, operator) => {
  let isMatch = false;

  if (operator === "isnull") {
    return dataColumnValue === null;
  }

  if (operator === "isnotnull") {
    return dataColumnValue !== null;
  }

  if (typeof dataColumnValue === "string") {
    if (operator === "eq") {
      isMatch = activeFilterValue === dataColumnValue;
    }

    if (operator === "neq") {
      isMatch = activeFilterValue !== dataColumnValue;
    }

    if (operator === "contains") {
      isMatch = dataColumnValue?.includes(activeFilterValue);
    }

    if (operator === "doesnotcontain") {
      isMatch = !dataColumnValue?.includes(activeFilterValue);
    }

    if (operator === "startswith") {
      isMatch = dataColumnValue.startsWith(activeFilterValue);
    }

    if (operator === "endswith") {
      isMatch = dataColumnValue?.endsWith(activeFilterValue);
    }

    if (operator === "isempty") {
      isMatch = dataColumnValue === "";
    }

    if (operator === "isnotempty") {
      isMatch = dataColumnValue !== "";
    }
  }

  return isMatch;
};

const operatorsWithEmptyValue = ["isnull", "isnotnull", "isempty", "isnotempty"];

const isFilterValid = (rowData, filter, fieldTypes) => {
  const { field, value, operator } = filter || {};
  const filterType = fieldTypes[field];
  const dataColumnValue = rowData[field];

  if (isEmptyValue(value) && !operatorsWithEmptyValue.includes(operator)) {
    return true;
  }

  if (filterType === "string") {
    return filterText(value?.toLowerCase() || "", dataColumnValue?.toLowerCase() || "", operator);
  }

  if (filterType === "number") {
    return filterNumber(value, dataColumnValue, operator);
  }

  if (filterType === "date") {
    return filterDate(value, dataColumnValue, operator);
  }

  if (filterType === "boolean") {
    return dataColumnValue === value;
  }

  return true;
};

const getFilteredRowByColumnFilter = (rowData, filters, logic, fieldTypes) => {
  const isFirstFilterValid = isFilterValid(rowData, filters[0], fieldTypes);
  if (!filters[1]) {
    return isFirstFilterValid;
  }
  else {
    const isSecondFilterValid = isFilterValid(rowData, filters[1], fieldTypes);
    if (logic === "or") {
      return isFirstFilterValid || isSecondFilterValid;
    }
    else {
      return isFirstFilterValid && isSecondFilterValid;
    }
  }
};

const sortData = (data, sort) => {
  const sortProp = sort.prop;
  const sortDirection = sort.dir === "desc" ? -1 : 1;
  return data.sort((a, b) => {
    if (a[sortProp] < b[sortProp]) {
      return -1 * sortDirection;
    }
    if (a[sortProp] > b[sortProp]) {
      return 1 * sortDirection;
    }
    return 0;
  });
};

const getParsedRows = (groupedData, parsedRowsToDisplay = [] as any[], nestedIndex = 0, rowIndex = { currentIndex: 0 }) => {
  groupedData?.map((rowData) => {
    if (rowData && rowData.groupedDataArr) {
      const { groupedByString, groupedDataArr, nestedIndex } = rowData;
      parsedRowsToDisplay.push({ groupedByString, nestedIndex });
      getParsedRows(groupedDataArr, parsedRowsToDisplay, nestedIndex, rowIndex);
    }
    else {
      parsedRowsToDisplay.push({ rowData, index: rowIndex.currentIndex, nestedIndex });
      rowIndex.currentIndex++;
    }
  });
  return parsedRowsToDisplay;
};

const getGroupedFilteredData = (remainingToGroupArr = [] as any[], dataToGroup = [] as any[], parentArr = [] as any[], nestedIndex = 0) => {
  if (remainingToGroupArr && remainingToGroupArr.length > 0 && dataToGroup) {
    const itemToGroupBy = remainingToGroupArr[0];
    const groupedData = {};
    dataToGroup?.map(rowData => {
      const groupedKey = `${convertCamelCase(itemToGroupBy)}: ${rowData[itemToGroupBy]}`;
      if (groupedData[groupedKey]) {
        groupedData[groupedKey].push(rowData);
      }
      else {
        groupedData[groupedKey] = [rowData];
      }
    });
    Object.entries(groupedData).map(([groupedByString, groupedDataArr]) => {
      if (groupedDataArr && Array.isArray(groupedDataArr) && groupedDataArr.length > 0) {
        parentArr.push({
          groupedByString,
          nestedIndex,
          groupedDataArr: remainingToGroupArr.length === 1 ? groupedDataArr : getGroupedFilteredData(remainingToGroupArr.slice(1), groupedDataArr, [], nestedIndex + 1)
        });
      }
    });
    return parentArr;
  }
  else {
    return dataToGroup;
  }
};

export const getLmsTableFilteredData = ({ dataToFilter, filters, globalLogic = "and", fieldTypes = {}, sort = [] }) => {
  let filteredData = dataToFilter;
  if (filters && filters?.length !== 0) {
    filteredData = dataToFilter.filter(d => {
      if (globalLogic === "and") {
        return filters?.every(({ filters, logic }) => getFilteredRowByColumnFilter(d, filters, logic, fieldTypes));
      }

      return filters?.some(({ filters, logic }) => getFilteredRowByColumnFilter(d, filters, logic, fieldTypes));
    });
  }

  if (sort?.length !== 0) {
    filteredData = sortData(filteredData, sort[0]);
  }

  return filteredData;
};

const parseJsonWithItemProp = (jsonStr) => {
  if (jsonStr && isJson(jsonStr)) {
    const parsedArray = jsonStr && isJson(jsonStr) ? JSON.parse(jsonStr) : [];
    return parsedArray.map((obj) => {
      const { prop, item } = getParsedPropItemFromLegacy(obj?.field);
      return {
        ...obj,
        item,
        prop
      };
    });
  }

  return [];
};

const getFormattedColumnFilter = (columnFilter) => {
  if (columnFilter?.field) {
    return { filters: [columnFilter] };
  }
  else {
    return columnFilter;
  }
};

const parseFiltersStr = (jsonStr) => {
  if (jsonStr && isJson(jsonStr)) {
    const parsedFilters = jsonStr && isJson(jsonStr) ? JSON.parse(jsonStr) : [];
    if (parsedFilters?.filters) {
      if (parsedFilters?.filters?.[0]?.field) {
        return { filters: [getFormattedColumnFilter(parsedFilters)] };
      }
      else {
        return { filters: parsedFilters?.filters?.map(columnFilter => getFormattedColumnFilter(columnFilter)) };
      }
    }
  }

  return {};
};

const getParsedValuesCurrentOptions = (currentViewOptions) => {
  return {
    currentViewOptions,
    currentViewSettings: currentViewOptions || {},
    currentViewFilters: parseFiltersStr(currentViewOptions?.filters),
    currentViewColumns: parseJsonWithItemProp(currentViewOptions?.columns),
    currentViewGroups: parseJsonWithItemProp(currentViewOptions?.groups),
    currentViewSort: parseJsonWithItemProp(currentViewOptions?.sort),
    searchValue: currentViewOptions?.search || "",
    currentViewIsAdmin: currentViewOptions?.isAdmin,
    currentViewTenantId: currentViewOptions?.tenantId || "",
    currentViewIsDefault: currentViewOptions?.isDefault,
    currentViewIsSystem: currentViewOptions?.isSystem,
    currentViewIsWidthSaved: currentViewOptions?.isWidthSaved,
    currentViewSearchValue: currentViewOptions?.search || ""
  };
};

export const LmsFilterReducer = (state: any, { type, payload }: any) => {
  const filtersSettingIsOpenStorage = `filterssettingsisopen${payload?.storageId || state?.storageId || window.location.pathname}`;
  let currentViewOptions: any;

  switch (type) {
    case GlobalConstants.BCStoreReducerCases.SET_USER:
      return {
        ...state,
        user: payload,
      };
    case GlobalConstants.BCStoreReducerCases.SET_STATE:
      return { ...payload };
    case GlobalConstants.BCStoreReducerCases.TOGGLE_FILTER_SETTINGS:
      return {
        ...state,
        isFilterSettingsOpen: !state.isFilterOpen,
      };
    case GlobalConstants.BCStoreReducerCases.OPEN_FILTER_SETTINGS:
      localStorageSave(filtersSettingIsOpenStorage, JSON.stringify(true), "session");
      return {
        ...state,
        isFilterSettingsOpen: true,
      };
    case GlobalConstants.BCStoreReducerCases.CLOSE_FILTER_SETTINGS:
      return {
        ...state,
        isFilterSettingsOpen: false,
      };
    case GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_FIELD_TYPES:
      return {
        ...state,
        currentViewFieldTypes: payload,
      };
    case GlobalConstants.BCStoreReducerCases.SET_SEARCH_VALUE:
      return {
        ...state,
        searchValue: payload,
        activePage: 1,
        firstEntryIndex: 0,
        lastEntryIndex: state.pagination,
      };
    case GlobalConstants.LmsTableReducerCases.SET_PAGINATION:
      return {
        ...state,
        pagination: payload,
        activePage: 1,
        firstEntryIndex: 0,
        lastEntryIndex: 1 * payload
      };
    case GlobalConstants.LmsTableReducerCases.SET_ACTIVE_PAGE:
      return {
        ...state,
        activePage: payload,
        firstEntryIndex: (payload * state.pagination) - state.pagination,
        lastEntryIndex: payload * state.pagination
      };
    case GlobalConstants.LmsTableReducerCases.RESET_ACTIVE_PAGE:
      return {
        ...state,
        activePage: 1,
        firstEntryIndex: 0,
        lastEntryIndex: state.pagination,
      };
    case GlobalConstants.LmsTableReducerCases.SET_COLUMNS:
      return {
        ...state,
        columns: payload,
      };
    case GlobalConstants.LmsTableReducerCases.SET_COLUMNS_LIST:
      return {
        ...state,
        columnsList: payload,
        columns: payload?.sort((a, b) => a.index - b.index).filter(col => !col.hidden),
      };
    case GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_VALUE:
      currentViewOptions = payload ? state.allViewOptions?.find(s => s.id === payload) : null;
      return {
        ...state,
        ...getParsedValuesCurrentOptions(currentViewOptions),
        currentViewValue: payload,
      };
    case GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_SETTINGS:
      return {
        ...state,
        currentViewSettings: payload
      };
    case GlobalConstants.BCStoreReducerCases.TOGGLE_FILTERING_DISABLED:
      return {
        ...state,
        isFilteringDisabled: !state.isFilteringDisabled,
      };
    case GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_FILTERS:
      return {
        ...state,
        currentViewFilters: payload,
      };
    case GlobalConstants.LmsTableReducerCases.SET_ALL_VIEW_OPTIONS:
      if (state.currentViewValue) {
        currentViewOptions = payload?.find(s => s.id === state.currentViewValue);
      }
      else {
        currentViewOptions = payload?.find(s => s.isDefault);
      }

      return {
        ...state,
        ...getParsedValuesCurrentOptions(currentViewOptions),
        allViewOptions: payload,
        currentViewValue: currentViewOptions?.id || "",
      };
    case GlobalConstants.LmsTableReducerCases.DELETE_CURRENT_VIEW_OPTION:
      currentViewOptions = state?.allViewOptions.find(s => s.isDefault);
      return {
        ...state,
        ...getParsedValuesCurrentOptions(currentViewOptions),
        allViewOptions: state?.allViewOptions?.filter(s => s.id !== payload) || [],
        currentViewValue: currentViewOptions?.id || "",
      };
    case GlobalConstants.LmsTableReducerCases.SET_FILTER_SUBMIT_MODAL:
      return {
        ...state,
        filterSubmitModal: payload
      };
    case GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_SORT:
      return {
        ...state,
        currentViewSort: payload
      };
    case GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_GROUPS:
      return {
        ...state,
        currentViewGroups: payload
      };
    case GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_COLUMNS:
      return {
        ...state,
        currentViewColumns: payload
      };
    case GlobalConstants.LmsTableReducerCases.SET_ITEMS:
      return {
        ...state,
        ...payload,
      };
    case GlobalConstants.BCStoreReducerCases.SET_INFO:
      return {
        ...state,
        info: payload as IInfoMessage,
      };
    case GlobalConstants.BCStoreReducerCases.CLEAR_INFO:
      return {
        ...state,
        info: null,
      };
    default:
      return state;
  }
};

export const useLmsGlobalFilter = (dataToFilter = [] as any[], options: ILmsLmsGlobalFilterOptions = {} as any) => {
  const { storageId = "", filterKeysList = [] as any[], useApiFiltering = false } = options;
  const [activeTab] = useLmsStoreSelector("activeTab");
  const gridUniqueIdString = useMemo(() => {
    if (storageId) {
      const uniqueId = getUniqueGridIdString(storageId || "");
      if (FeatureFlags.IS_LMSv2_DEBUG) console.log(uniqueId);
      return uniqueId;
    }
    else {
      return "";
    }
  }, [storageId]);

  const [state, tableCacheDispatch] = useLMSGlobalReducer();
  const tableCache = state?.tableCache?.[activeTab?.id]?.[gridUniqueIdString] || {};

  const setTableCacheProperty = (property, value) => {
    tableCacheDispatch({
      type: GlobalConstants.LMSStoreReducerCases.SET_TABLE_CACHE,
      payload: { tableId: gridUniqueIdString, activeTabId: activeTab?.id, data: {...tableCache, [property]: value} }
    });
  };

  const setTableCache = (payload) => {
    tableCacheDispatch({
      type: GlobalConstants.LMSStoreReducerCases.SET_TABLE_CACHE,
      payload: { tableId: gridUniqueIdString, activeTabId: activeTab?.id, data: payload }
    });
  };

  const [totalGridCount, setTotalGridCount] = useState(0);
  const [filteredData, setFilteredData] = useState(dataToFilter);
  const [apiFilterHeaders, setApiFilterHeaders] = useState(undefined as any);
  const updateGridCount = useLmsCacheSelector(storageId, undefined, true)[1];
  const searchStorageId = `searchterm${storageId ? storageId + window.location.pathname : window.location.pathname}`;
  const currentViewStorageId = `viewvalue${storageId ? storageId + window.location.pathname : window.location.pathname}`;
  const initialState = {
      isFilterOpen: localStorageGet(`filtersisopen${storageId || window.location.pathname}`, "session") || false,
      pagination: GlobalConstants.DefaultValues.tablePagination,
      searchValue: "",
      firstEntryIndex: 0,
      lastEntryIndex: GlobalConstants.DefaultValues.tablePagination,
      activePage: 1,
      searchStorageId: searchStorageId,
      currentViewStorageId: currentViewStorageId,
      columns: [],
      columnsList: [],
      allViewOptions: [],
      currentViewSettings: {},
      currentViewFilters: { filters: [] },
      isFilteringDisabled: false,
      currentViewColumns: [],
      currentViewGroups: [],
      currentViewIsAdmin: false,
      currentViewTenantId: "",
      currentViewIsDefault: false,
      currentViewIsSystem: false,
      currentViewIsWidthSaved: false,
      currentViewSearchValue: null as any,
      filterSubmitModal: { isVisible: false, posX: "", posY: "" },
      ...options?.initialValues || {}
    };

  const [reducerStore, dispatch]: any = useReducer(LmsFilterReducer, { ...initialState });
  const {
    searchValue,
    firstEntryIndex,
    lastEntryIndex,
    activePage,
    currentViewFilters,
    currentViewFieldTypes,
    isFilteringDisabled,
    currentViewColumns,
    currentViewGroups,
    currentViewSort,
    currentViewIsAdmin,
    currentViewTenantId,
    currentViewIsDefault,
    currentViewIsSystem,
    currentViewIsWidthSaved,
    allViewOptions
  } = reducerStore;

  const setItems = (payload: any[]) => {
    dispatch({ type: GlobalConstants.LmsTableReducerCases.SET_ITEMS, payload: payload });
    setTableCache({ ...tableCache, ...payload });
  };

  const resetActivePage = () => {
    dispatch({ type: GlobalConstants.LmsTableReducerCases.RESET_ACTIVE_PAGE });
    setTableCacheProperty("activePage", 1);
  };

  const setSearchValue = (value: string, skipCache = false) => {
    dispatch({ type: GlobalConstants.LmsTableReducerCases.SET_SEARCH_VALUE, payload: value });

    if (!skipCache) {
      setTableCacheProperty("searchValue", value);
    }
  };

  const setPagination = (value: number) => {
    dispatch({ type: GlobalConstants.LmsTableReducerCases.SET_PAGINATION, payload: value });
    setTableCache({ ...tableCache, activePage: 1, pagination: value, firstEntryIndex: 0, lastEntryIndex: value });
  };

  const setActivePage = (value: number, skipCache = false) => {
    dispatch({ type: GlobalConstants.LmsTableReducerCases.SET_ACTIVE_PAGE, payload: value });
    const paginationDefault = tableCache?.pagination || GlobalConstants.DefaultValues.tablePagination;

    if (!skipCache) {
      setTableCache({
        ...tableCache || {},
        activePage: value,
        firstEntryIndex: (value * paginationDefault) - paginationDefault,
        lastEntryIndex: value * paginationDefault,
      });
    }
  };

  const setFirstEntryIndex = (value: number) => {
    dispatch({ type: GlobalConstants.LmsTableReducerCases.SET_FIRST_ENTRY_INDEX, payload: value });
  };

  const setLastEntryIndex = (value: number) => {
    dispatch({ type: GlobalConstants.LmsTableReducerCases.SET_LAST_ENTRY_INDEX, payload: value });
  };

  const setColumns = (value: any[], skipCache = false) => {
    dispatch({ type: GlobalConstants.LmsTableReducerCases.SET_ITEMS, payload: { columns: value } });
    if (value?.length > 0 && !skipCache) {
      setTableCacheProperty("columns", value);
    }
  };

  useEffect(() => {
    setItems({ ...tableCache });
    if (tableCache?.columns) {
      setColumns(tableCache?.columns, true);
    }
  }, [activeTab?.id]);

  const setColumnsList = (value: any[], skipCache = false) => {
    dispatch({ type: GlobalConstants.LmsTableReducerCases.SET_COLUMNS_LIST, payload: value });
    if (value?.length > 0 && !skipCache) {
      setTableCache({ ...tableCache, columnsList: value, columns: value?.filter(col => !col.hidden) });
    }
  };

  const formattedCommonFilters = {
    filters: currentViewFilters?.filters || [],
    sort: currentViewSort || [],
    search: searchValue,
  };

  const blankFilter = JSON.stringify({
    filters: [],
    sort: [],
    search: "",
  });

  const formattedFilters = {
    ...formattedCommonFilters,
    groups: currentViewGroups,
    columns: currentViewColumns,
    isAdmin: currentViewIsAdmin,
    tenantId: currentViewTenantId,
    isDefault: currentViewIsDefault,
    isSystem: currentViewIsSystem,
    isWidthSaved: currentViewIsWidthSaved,
  };

  const formattedFilterWithStrings = {
    ...formattedFilters,
    filters: JSON.stringify(currentViewFilters || {}),
    groups: JSON.stringify(currentViewGroups || []),
    columns: JSON.stringify(currentViewColumns || []),
    sort: JSON.stringify(currentViewSort || []),
  };

  const getFormattedApiFilters = (encode = false, getCount = false) => {
    const filterHeaderPayload = {
      ...formattedCommonFilters,
      start: firstEntryIndex,
      end: lastEntryIndex - 1,
      getCount,
    };

    if (encode) {
      return base64url_encode(JSON.stringify(filterHeaderPayload));
    }
    else {
      return JSON.stringify(filterHeaderPayload);
    }
  };

  const getFilteredData = (
    {
      dataToFilter,
      filters = currentViewFilters?.filters,
      globalLogic = currentViewFilters?.logic,
      fieldTypes = currentViewFieldTypes,
      sort = currentViewSort,
    }) => {
    if (dataToFilter) {
      return getLmsTableFilteredData({ dataToFilter, filters, globalLogic, fieldTypes, sort });
    }
    else {
      return [];
    }
  };

  const filterBySearchValue = (dataToFilter = [], filterKeys = filterKeysList) => {
    if (searchValue) {
      return dataToFilter?.filter(x => Object.entries(x).some(([k, v]: any) => {
        if (v && typeof v === "string") {
          return v.toLowerCase().includes(searchValue?.toLowerCase()) && (filterKeys.length === 0 || filterKeys.includes(k));
        }

        return false;
      }));
    }

    return dataToFilter;
  };

  const setCurrentViewGroups = (groups: any) => {
    dispatch({ type: GlobalConstants.LmsTableReducerCases.SET_CURRENT_VIEW_GROUPS, payload: groups });
  };

  const updateFilterData = (filteredDataTemp) => {
    if (currentViewGroups?.length !== 0) {
      setFilteredData(getParsedRows(getGroupedFilteredData(currentViewGroups?.map(gi => gi.prop), filteredDataTemp, [], 0)));
    }
    else {
      setFilteredData(filteredDataTemp);
    }
  };

  useEffect(() => {
    if (!useApiFiltering) {
      updateFilterData(filterBySearchValue(getFilteredData({ dataToFilter })));
    }
  }, [searchValue, dataToFilter]);

  useEffect(() => {
    if (!useApiFiltering) {
      if (currentViewFilters && currentViewFieldTypes) {
        if (isFilteringDisabled) {
          setFilteredData(dataToFilter);
        }
        else {
          updateFilterData(filterBySearchValue(getFilteredData({ dataToFilter })));
        }
      }
    }
    else {
      updateFilterData(dataToFilter);
    }
  }, [currentViewFilters?.filters, currentViewFieldTypes, isFilteringDisabled, currentViewSort, currentViewGroups, dataToFilter]);

  const addApiFilterHeaders = (getCount) => {
    if (firstEntryIndex !== lastEntryIndex && allViewOptions?.length > 0) {
      if (isFilteringDisabled) {
        setApiFilterHeaders(blankFilter);
      }
      else if (blankFilter !== getFormattedApiFilters()) {
        setApiFilterHeaders({ filter: getFormattedApiFilters(true, getCount) });
      }
    }
    else if (apiFilterHeaders) {
      setApiFilterHeaders(null);
    }
  };

  useEffect(() => {
    if (useApiFiltering) {
      addApiFilterHeaders(true);
    }

    if (firstEntryIndex !== 0 || activePage !== 1) {
      setActivePage(1);
    }
  }, [currentViewFilters?.filters, isFilteringDisabled, currentViewSort, searchValue]);

  useEffect(() => {
    if (useApiFiltering) {
      if (apiFilterHeaders) {
        addApiFilterHeaders(false);
      }
      else {
        addApiFilterHeaders(true);
      }
    }
  }, [firstEntryIndex, lastEntryIndex]);

  useEffect(() => {
    if (!useApiFiltering && filteredData) {
       setTotalGridCount(filteredData?.length);
    }
  }, [filteredData?.length]);

  useEffect(() => {
    if (!isEmptyValue(totalGridCount) && storageId) {
      updateGridCount(totalGridCount);
    }
  }, [totalGridCount]);

  const filterPageData = useApiFiltering ? filteredData : filteredData.slice(firstEntryIndex, lastEntryIndex);

  return [{
    ...reducerStore,
    activeTabId: activeTab?.id,
    setColumns,
    setColumnsList,
    setFirstEntryIndex,
    totalGridCount,
    setTotalGridCount,
    lastEntryIndex,
    setLastEntryIndex,
    setSearchValue,
    setPagination,
    setActivePage,
    resetActivePage,
    formattedFilters,
    apiFilterHeaders,
    formattedFilterWithStrings,
    setCurrentViewGroups,
    filteredData,
    filterPageData,
    dataToFilter,
    getFilteredData,
    filterBySearchValue,
    setFilteredData,
    currentViewSort
  }, dispatch] as [any, (any) => any];
};